"use client";

import { useCallback, useMemo, useState, useEffect, useRef } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import useMostRecentViews from "@/hooks/useMostRecentViews";
import { Title, Box as MantineBox } from "@mantine/core";
import { notifications } from "@mantine/notifications";

import { translateText } from "@/lib/client/translate";
import { loadCurrentProduct } from "@/lib/client/product";
import { getItemTimestampDisplay } from "@/lib/client/utils";
import { useRelatedProducts, useVisuallySimilar } from "@/hooks/useRelatedProducts";
import useGetSlideinDetails from "@/hooks/useGetSlideinDetails";
import { usePriceInsights } from "@/hooks/usePriceInsights";
import { usePriceMonitorRequest } from "@/hooks/usePriceMonitorRequest";
import { useFiltersStore } from "@/stores/filtersStore";
import { useCollectionsStore } from "@/stores/collectionsStore";
import { useUiStore } from "@/stores/uiStore";
import { usePriceDropStore } from "@/stores/priceDropStore";
import { useGlobalData } from "@/context/globalContext";
import { useAuthContext } from "@/context/authContext";
import { trackItemOutboundClick } from "@/lib/client/itemTracking";
import { buildItemDescription } from "@/lib/shared/itemDescription";
import { generateBrandSlug } from "@/lib/shared/utils";
//import FollowButton from "@/components/FollowButton";

import { useCopyToClipboard } from "@/hooks/useCopyToClipboard";
import { useImageSearchStore } from "@/stores/imageSearchStore";
import { useDuplicateListings } from "@/hooks/useDuplicateListings";
import ItemCarousel from "@/components/ItemCarousel";
import Breadcrumb from "@/components/Breadcrumb";
import Modal from "@/components/Modal";
import SizeTable from "@/components/SizeTable";
import {
  ItemDetails,
  ItemGallery,
  ItemHeaderActions,
  ItemReportButton,
} from "@/components/ItemDetails";
import styles from "./styles.module.scss";
import ReportListingModal from "@/components/ReportListingModal";

const CT_CONDITION_LABELS: Record<number, string> = {
  5: "New",
  4: "Like new",
  3: "Good",
  2: "Used",
  1: "Heavily used",
};

const ProductPage = ({ item: initialItem }: { item: SearchResultItem }) => {
  const [item, setItem] = useState(initialItem);
  const [title, setTitle] = useState(initialItem.title || "");
  const [description, setDescription] = useState(
    buildItemDescription(initialItem.title, initialItem.description)
  );
  const [price, setPrice] = useState(item?.price);
  const [currency, setCurrency] = useState("");
  const [loading, setLoading] = useState(false);
  const [translated, setTranslated] = useState(false);
  const [showSizeModal, setShowSizeModal] = useState(false);

  const router = useRouter();
  const searchParams = useSearchParams();
  const priceMonitorAutoOpenedRef = useRef(false);
  const favoriteRequestInFlightRef = useRef(false);
  const { updateMostRecent } = useMostRecentViews();
  const activeFilters = useFiltersStore((state) => state.activeFilters);
  const { exchangeRates, proxies: proxiesByCountry } = useGlobalData();
  const { isSignedIn, isLoaded, isProUser } = useAuthContext();
  const priceInsights = usePriceInsights(item);
  const { requestPriceMonitor } = usePriceMonitorRequest();
  const {
    duplicates,
    loading: duplicatesLoading,
    hasSearched,
    error: duplicatesError,
    retry: retryDuplicates,
  } = useDuplicateListings(item, true);
  const isMonitored = usePriceDropStore((state) => (item?.id ? state.isMonitored(item.id) : false));
  const { openWarningModal } = useUiStore((state) => state.actions);
  const { copied: showCopied, copyToClipboard } = useCopyToClipboard();
  const { startSearch: startImageSearch } = useImageSearchStore((state) => state.actions);

  const [reportModalOpen, setReportModalOpen] = useState(false);

  const activeCollectionItemIds = useCollectionsStore((state) => state.activeCollectionItemIds);
  const { loadCollections, optimisticallyRemoveItem, fetchItemLikedBy } = useCollectionsStore(
    (state) => state.actions
  );

  const isFavorited = item?.id ? activeCollectionItemIds.has(item.id) : false;

  const { extraDetails, loading: loadingExtra } = useGetSlideinDetails(item?.id);
  const displayCondition =
    extraDetails?.ctcondition != null
      ? (CT_CONDITION_LABELS[extraDetails.ctcondition] ?? extraDetails.condition)
      : (extraDetails?.condition ??
        (item?.ctcondition != null
          ? (CT_CONDITION_LABELS[item.ctcondition] ?? item?.condition)
          : item?.condition));

  const { visualProducts, loading: visualLoading } = useVisuallySimilar({
    id: item?.id,
    currency,
  });
  const { products, loading: relatedLoading } = useRelatedProducts({
    id: item?.id,
    currency,
  });

  useEffect(() => {
    setItem(initialItem);

    const controller = new AbortController();

    loadCurrentProduct(initialItem.id, controller.signal)
      .then((currentItem) => {
        if (currentItem) setItem(currentItem);
      })
      .catch((error) => {
        if (!controller.signal.aborted) {
          console.error("Failed to refresh product:", error);
        }
      });

    return () => controller.abort();
  }, [initialItem]);

  useEffect(() => {
    const filteredCurrency = activeFilters?.currency?.[0]?.value as string;
    if (!filteredCurrency || !exchangeRates?.[filteredCurrency]) return;

    if (filteredCurrency === item?.currency) {
      // Already the requested currency — the listing's own native price is correct as-is.
      setPrice(item.price);
      setCurrency(item.currency);
      return;
    }

    // item.price/item.currency are the listing's own native currency (can be
    // anything — EUR, JPY, KRW, ...), while exchangeRates is a USD-based rate
    // table (units of X per 1 USD). Converting from item.price directly mixed
    // two different currencies together; item.priceusd is the canonical,
    // FX-stabilized USD price and is the correct base to convert from.
    const convertedPrice = Math.floor(Number(item.priceusd) * exchangeRates[filteredCurrency]);
    setPrice(convertedPrice.toString());
    setCurrency(filteredCurrency);
  }, [activeFilters, exchangeRates, item?.currency, item?.price, item?.priceusd]);

  const handlePriceDropCta = useCallback(() => {
    if (!item) return;

    requestPriceMonitor(item, { source: "product" });
  }, [item, requestPriceMonitor]);

  // Deep-link entry point: ?openPriceMonitor=1 (e.g. a Discord post's "Price
  // Watch" button) auto-opens the same "get notified if the price drops"
  // panel this page's own CTA opens. Wait for Clerk to resolve so the shared
  // request flow can either open immediately or retain the request through
  // native/web sign-in without navigating away from this item.
  useEffect(() => {
    if (priceMonitorAutoOpenedRef.current) return;
    if (searchParams.get("openPriceMonitor") !== "1") return;
    if (!item || !isLoaded) return;

    priceMonitorAutoOpenedRef.current = true;
    handlePriceDropCta();
  }, [handlePriceDropCta, isLoaded, item, searchParams]);

  const translateItem = async () => {
    setLoading(true);
    try {
      const [translatedTitle, translatedDescription] = await Promise.all([
        translateText(title),
        description ? translateText(description) : Promise.resolve(null),
      ]);
      if (translatedTitle) {
        setTitle(translatedTitle);
      }
      if (translatedDescription) {
        setDescription(translatedDescription);
      }
      setTranslated(!!translatedTitle && (!description || !!translatedDescription));
    } catch (error) {
      console.log(error);
    }
    setLoading(false);
  };

  const handleFindSimilar = () => {
    if (item?.imageurl) {
      startImageSearch(item, null);
      router.push("/search?clearFilters=1");
    }
  };

  const handleAppraiseItem = () => {
    if (!item) return;
    // Same deep-link entry point as ItemSlideIn's appraise button —
    // usePriceIntelligence.ts's prefill effect fills in brand/condition/size/
    // marketplace as best it can from this listing.
    router.push(`/appraise?itemId=${item.id}`);
  };

  const handleFavorite = async (e: React.SyntheticEvent) => {
    e.stopPropagation();

    if (!item || !isSignedIn) {
      return;
    }

    if (favoriteRequestInFlightRef.current) {
      return;
    }

    favoriteRequestInFlightRef.current = true;

    try {
      if (isFavorited) {
        const result = await optimisticallyRemoveItem(item.id);
        if (!result.success) {
          notifications.show({
            title: "Couldn't remove saved item",
            message: result.error || "Please try again.",
            color: "red",
            position: "top-center",
          });
        }
      } else {
        const currentCollectionsState = useCollectionsStore.getState();
        const hasActiveCollection = currentCollectionsState.collections.some(
          (collection) => collection.id === currentCollectionsState.activeCollectionId
        );

        if (!hasActiveCollection) {
          await loadCollections();
        }

        const latestCollectionsState = useCollectionsStore.getState();
        if (!latestCollectionsState.activeCollectionId) {
          notifications.show({
            title: "Couldn't save item",
            message: "Your favorites collection could not be loaded. Please try again.",
            color: "red",
            position: "top-center",
          });
          return;
        }

        const result = await latestCollectionsState.actions.optimisticallyAddItem(item.id, item);
        if (!result.success) {
          notifications.show({
            title: "Couldn't save item",
            message: result.error || "Please try again.",
            color: "red",
            position: "top-center",
          });
        }
      }
    } catch (error) {
      console.error("Error handling favorite:", error);
      notifications.show({
        title: isFavorited ? "Couldn't remove saved item" : "Couldn't save item",
        message: "Please try again.",
        color: "red",
        position: "top-center",
      });
    } finally {
      favoriteRequestInFlightRef.current = false;
    }
  };

  useEffect(() => {
    setTitle(item?.title || "");
    setDescription(buildItemDescription(item?.title, item?.description));
    setPrice(Math.floor(parseFloat(item?.price || "0")).toString());
    setTranslated(false);
  }, [item]);

  useEffect(() => {
    if (extraDetails?.itemdescription) {
      setDescription(buildItemDescription(item?.title, extraDetails.itemdescription));
    }
  }, [extraDetails?.itemdescription, item?.title]);

  useEffect(() => {
    if (item?.id) {
      fetchItemLikedBy(item.id);
    }
  }, [item?.id, fetchItemLikedBy]);

  useEffect(() => {
    if (item?.id) {
      updateMostRecent(item.id);
    }
  }, [item?.id, updateMostRecent]);

  const isSold = item?.isSold;
  const images = useMemo(() => {
    const base = item?.gallery?.length ? item.gallery : item?.imageurl ? [item.imageurl] : [];
    const extra = (extraDetails?.additionalimages ?? []).filter(Boolean) as string[];
    const combined = [...base, ...extra];
    return [...new Set(combined)];
  }, [item, extraDetails]);
  const timestampDisplay =
    item &&
    getItemTimestampDisplay({
      item,
      showLabel: true,
      showAddedWhenUpdated: true,
    });

  if (!item) return "No item found";

  const breadcrumbs = [
    { text: "Search", link: "/search" },
    {
      text: item?.brand?.label,
      link: `/brands/${generateBrandSlug(item?.brand?.label || "")}`,
    },
  ];

  return (
    <MantineBox className={styles.container}>
      <Modal isOpen={showSizeModal} closeModal={() => setShowSizeModal(false)}>
        <SizeTable />
      </Modal>

      <ReportListingModal
        isOpen={reportModalOpen}
        toggleOpen={() => setReportModalOpen(!reportModalOpen)}
        listingId={item?.id || 0}
      />

      {/* ── Sticky header ── */}
      <MantineBox className={styles.stickyHeader}>
        <Breadcrumb items={breadcrumbs} className={styles.productBreadcrumb} />
        <Title order={2} className={styles.h2NotBold}>
          {title}
        </Title>
        <ItemHeaderActions
          className={styles.productHeaderActions}
          hasImage={!!item.imageurl}
          isSold={isSold}
          isSignedIn={isSignedIn}
          isFavorited={isFavorited}
          shareLabel={showCopied ? "COPIED!" : "SHARE"}
          onFindSimilar={handleFindSimilar}
          onFavorite={handleFavorite}
          onShare={() =>
            copyToClipboard(
              `${window.location.protocol || "https:"}//${window.location.host}/search/product/${item.id}`
            )
          }
        />
      </MantineBox>

      <MantineBox className={styles.productPage}>
        <MantineBox className={styles.imageColumn}>
          <ItemGallery images={images} itemId={item.id} title={item.title} layout="page" />
        </MantineBox>
        <MantineBox className={styles.body}>
          <ItemDetails
            item={item}
            price={price}
            currencyCode={currency}
            description={description}
            descriptionLoading={loadingExtra}
            condition={displayCondition}
            priceInsights={priceInsights}
            duplicates={{
              items: duplicates,
              loading: duplicatesLoading,
              hasSearched,
              error: duplicatesError,
              retry: retryDuplicates,
            }}
            proxies={proxiesByCountry}
            isFavorited={isFavorited}
            isMonitored={isMonitored}
            isProUser={isProUser}
            translationLoading={loading}
            translated={translated}
            timestampDisplay={timestampDisplay}
            onAppraise={handleAppraiseItem}
            onBuy={() => {
              try {
                trackItemOutboundClick(item, "product_page");
              } catch {
                // Analytics must never affect outbound navigation
              }
            }}
            onOpenPriceMonitor={handlePriceDropCta}
            onOpenSizeGuide={() => setShowSizeModal(true)}
            onOpenWarning={openWarningModal}
            onTranslate={translateItem}
          />
          {(visualLoading || !!visualProducts?.length) && (
            <MantineBox className={styles.recommendationsContainer}>
              <ItemCarousel
                items={visualProducts}
                ghosts={4}
                routeToProductPage
                loading={visualLoading}
                title="YOU MIGHT LIKE THESE"
                desktopCompact
              />
            </MantineBox>
          )}
        </MantineBox>
      </MantineBox>
      {(relatedLoading || !!products?.length) && (
        <MantineBox className={styles.carouselContainer}>
          <ItemCarousel
            items={products}
            ghosts={8}
            routeToProductPage
            loading={relatedLoading}
            title={`MORE FROM ${item?.brand?.label?.toUpperCase() || "THIS BRAND"}`}
            insetNavButtons
          />
        </MantineBox>
      )}
      <ItemReportButton onClick={() => setReportModalOpen(true)} />
    </MantineBox>
  );
};

export default ProductPage;
